import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { hasInAppHistory } from '../hooks/useSmartBack';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Save, X, Loader2,
  Check, Camera
} from 'lucide-react';
import { toast } from 'sonner';
import { SelectField } from '../components/SelectField';
import AppHeader from '../components/AppHeader';
// iter291 — Edit must use the SAME source-of-truth as the orange + posting
// flow (AddAd.js → constants/categories). The previous reduced
// `config/categories.CATEGORY_ORDER` list (26 cats) was missing 7 cats
// the AddAd flow ships, so users editing drafts saw a different list.
import { categories } from '../constants/categories';
import SUBCATEGORIES from '../data/subcategories';
import './EditAd.css';
import { getApiBase } from '../api/httpClient';
import { normalizeInternationalPhone } from '../utils/phone';

// iter282 — getApiBase() returns '' on live custom domains (wassla.tech)
// so `${BACKEND_URL}/api/upload` becomes a relative `/api/upload` and
// the live ingress proxies it same-origin. Preview + dev keep the
// build-time absolute URL because they need the cross-origin reach.
const BACKEND_URL = getApiBase();

const CURRENCIES = [
  { code: 'SDG', en: 'SDG — Sudanese Pound', ar: 'SDG — جنيه سوداني' },
  { code: 'SSP', en: 'SSP — South Sudanese Pound', ar: 'SSP — جنيب جنوب سوداني' },
  { code: 'USD', en: 'USD — US Dollar', ar: 'USD — دولار أمريكي' },
  { code: 'EUR', en: 'EUR — Euro', ar: 'EUR — يورو' },
  { code: 'GBP', en: 'GBP — British Pound', ar: 'GBP — جنيه إسترليني' },
  { code: 'AED', en: 'AED — UAE Dirham', ar: 'AED — درهم إماراتي' },
  { code: 'SAR', en: 'SAR — Saudi Riyal', ar: 'SAR — ريال سعودي' },
  { code: 'EGP', en: 'EGP — Egyptian Pound', ar: 'EGP — جنيه مصري' },
  { code: 'QAR', en: 'QAR — Qatari Riyal', ar: 'QAR — ريال قطري' },
  { code: 'KWD', en: 'KWD — Kuwaiti Dinar', ar: 'KWD — دينار كويتي' },
  { code: 'BHD', en: 'BHD — Bahraini Dinar', ar: 'BHD — دينار بحريني' },
  { code: 'OMR', en: 'OMR — Omani Rial', ar: 'OMR — ريال عماني' },
  { code: 'KES', en: 'KES — Kenyan Shilling', ar: 'KES — شلن كيني' },
  { code: 'UGX', en: 'UGX — Ugandan Shilling', ar: 'UGX — شلن أوغندي' },
  { code: 'TZS', en: 'TZS — Tanzanian Shilling', ar: 'TZS — شلن تنزاني' },
  { code: 'ETB', en: 'ETB — Ethiopian Birr', ar: 'ETB — بر إثيوبي' },
  { code: 'NGN', en: 'NGN — Nigerian Naira', ar: 'NGN — نيرا نيجيري' },
  { code: 'ZAR', en: 'ZAR — South African Rand', ar: 'ZAR — راند جنوب أفريقي' },
  { code: 'LYD', en: 'LYD — Libyan Dinar', ar: 'LYD — دينار ليبي' },
  { code: 'CDF', en: 'CDF — Congolese Franc', ar: 'CDF — فرنك كونغولي' },
];

const CONDITIONS = [
  { value: 'new', en: 'New', ar: 'جديد' },
  { value: 'like_new', en: 'Like New', ar: 'كالجديد' },
  { value: 'good', en: 'Good', ar: 'جيد' },
  { value: 'fair', en: 'Fair', ar: 'مقبول' },
];

export default function EditAd() {
  const { adId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const { token } = useAuth();
  const isArabic = lang === 'ar';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [originalAd, setOriginalAd] = useState(null);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [discount, setDiscount] = useState('');
  const [currency, setCurrency] = useState('SDG');
  const [condition, setCondition] = useState('');
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [location, setLocation] = useState('');
  const [images, setImages] = useState([]);
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [subcategories, setSubcategories] = useState([]);
  // iter291 — Hold the full set of vertical / service / hub fields the
  // user originally chose so Save Changes round-trips them losslessly.
  // We never expose UI for these on the lightweight Edit form, but we
  // MUST preserve them on the wire so a Service ad doesn't get
  // demoted to a Product ad on save (which is what was happening on
  // live and producing "Update failed — check your data").
  const [preservedFields, setPreservedFields] = useState({});

  const txt = (ar, en) => (isArabic ? ar : en);

  // Build options for shared SelectField component.
  // iter291 — Use the same `categories` array AddAd.js uses, with the
  // same key/en/ar/aliases shape, so the dropdown is byte-for-byte
  // identical between New Listing and Edit Draft.
  const categoryOptions = useMemo(() => [
    ...categories.map(cat => ({
      value: cat.key,
      label: cat.en,
      label_ar: cat.ar,
      icon: cat.icon,
      aliases: cat.aliases,
    })),
    { value: 'other', label: 'Other', label_ar: 'أخرى' },
  ], []);

  const currencyOptions = useMemo(() =>
    CURRENCIES.map(c => ({ value: c.code, label: c.en, label_ar: c.ar })),
  []);

  const subcategoryOptions = useMemo(() =>
    subcategories.map(s => ({
      value: s.id || s.key || s,
      label: s.name_en || s.en || s.name || String(s),
      label_ar: s.name_ar || s.ar || s.name || String(s),
    })),
  [subcategories]);

  // Fetch ad data
  useEffect(() => {
    if (!adId || !token) return;
    const fetchAd = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/ads/${adId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Ad not found');
        const data = await res.json();
        const ad = data.ad || data;
        setOriginalAd(ad);
        // iter291 — `draft_data` is the raw blob the AddAd form
        // originally sent (kept as a safety net by the backend). If a
        // top-level promoted field is missing (legacy drafts saved
        // before iter291), fall back to draft_data.
        const dd = ad.draft_data || {};
        const pick = (k, ...alts) => {
          if (ad[k] !== undefined && ad[k] !== null && ad[k] !== '') return ad[k];
          for (const a of alts) {
            if (dd[a] !== undefined && dd[a] !== null && dd[a] !== '') return dd[a];
          }
          return undefined;
        };
        setTitle(pick('title') || '');
        setDescription(pick('description') || '');
        const _price = pick('price');
        setPrice(_price ? String(_price) : '');
        const _disc = pick('discount_percent', 'discountPercent', 'discount');
        setDiscount(_disc ? String(_disc) : '');
        setCurrency(pick('currency') || 'SDG');
        setCondition(pick('condition') || '');
        setCategory(pick('category') || '');
        setSubcategory(pick('subcategory_id', 'subcategory_id', 'subcategory') || pick('subcategory') || '');
        setLocation(pick('location', 'city', 'city_en') || '');
        setImages(ad.images || []);
        setPhone(pick('seller_phone', 'sellerPhone') || '');
        setWhatsapp(pick('seller_whatsapp', 'sellerWhatsapp') || '');
        setEmail(pick('seller_email', 'sellerEmail') || '');
        // iter291 — Preserve every vertical / service / hub field for
        // lossless save round-trip.
        setPreservedFields({
          listing_type: pick('listing_type') || 'product',
          service_category: pick('service_category'),
          service_subcategory: pick('service_subcategory'),
          service_options: pick('service_options'),
          price_type: pick('price_type'),
          car_attrs: pick('car_attrs'),
          phone_attrs: pick('phone_attrs'),
          property_attrs: pick('property_attrs'),
          hub: pick('hub'),
          visibility: pick('visibility'),
          category_main: pick('category_main'),
          category_sub: pick('category_sub'),
          category_path: pick('category_path'),
          hub_category_main: pick('hub_category_main'),
          hub_category_sub: pick('hub_category_sub'),
          brand_type: pick('brand_type'),
          latitude: pick('latitude'),
          longitude: pick('longitude'),
          city: pick('city'),
          state: pick('state'),
          availability_status: pick('availability_status'),
        });
      } catch (err) {
        toast.error(txt('حدث خطأ في تحميل الإعلان', 'Failed to load ad'));
        if (hasInAppHistory()) navigate(-1);
        else navigate('/my-ads', { replace: true });
      } finally {
        setLoading(false);
      }
    };
    fetchAd();
  }, [adId, token]);

  // Subcategories from local config
  useEffect(() => {
    if (!category) { setSubcategories([]); return; }
    const subs = SUBCATEGORIES[category] || [];
    setSubcategories(subs);
  }, [category]);

  // ─── IMAGE UPLOAD — uses same endpoint as AddAd: POST /api/upload with "files" field ───
  const handleImageUpload = useCallback(async (e) => {
    const fileList = Array.from(e.target.files || []);
    if (!fileList.length) return;
    if (images.length + fileList.length > 8) {
      toast.error(txt('الحد الأقصى ٨ صور', 'Maximum 8 images'));
      return;
    }
    setUploading(true);
    const newImages = [];
    for (const file of fileList) {
      if (!file.type?.startsWith('image/')) {
        toast.error(txt('نوع الملف غير مدعوم', 'Unsupported file type'));
        continue;
      }
      const formData = new FormData();
      formData.append('files', file); // "files" plural — matches backend parameter name
      try {
        const res = await fetch(`${BACKEND_URL}/api/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          console.error('[Upload] failed:', err);
          // iter282 — surface the real backend error to help operators
          // pinpoint failure type (cloudinary off / disk full / 401 / etc.)
          const detailObj = (err && typeof err.detail === 'object') ? err.detail : null;
          const firstErr = detailObj?.errors?.[0]?.error;
          const diag = detailObj?.diagnostic
            ? ` [cloudinary=${detailObj.diagnostic.cloudinary_configured ? 'on' : 'off'}]`
            : '';
          const msg = firstErr
            ? `${txt('فشل رفع الصورة', 'Image upload failed')}: ${firstErr}${diag}`
            : (err?.detail?.message || `${txt('فشل رفع الصورة', 'Image upload failed')} (HTTP ${res.status})${diag}`);
          toast.error(msg);
          continue;
        }
        const data = await res.json();
        // Backend returns { images: ["/api/uploads/xyz.jpg" or "https://res.cloudinary.com/..."] }
        if (data.images?.length > 0) {
          const url = data.images[0];
          // iter282 — store the URL as-is. Absolute URLs (Cloudinary)
          // stay absolute. Relative /api/uploads/... stay relative; the
          // ad renderer (lib/media.js#toAbsoluteUrl) will expand them
          // to same-origin at render time on live.
          newImages.push(url);
        }
      } catch (err) {
        console.error('[Upload] Network error:', err);
        toast.error(`${txt('فشل رفع الصورة — تحقق من الاتصال', 'Upload failed — check connection')}: ${err?.message || err}`);
      }
    }
    if (newImages.length > 0) {
      setImages(prev => [...prev, ...newImages]);
      toast.success(txt(`تم رفع ${newImages.length} صورة`, `${newImages.length} image(s) uploaded`));
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [images.length, token, isArabic]);

  const removeImage = (index) => setImages(prev => prev.filter((_, i) => i !== index));

  // Validate
  const validate = () => {
    const errs = {};
    if (!title.trim()) errs.title = txt('العنوان مطلوب', 'Title is required');
    if (!category) errs.category = txt('التصنيف مطلوب', 'Please choose a category');
    // iter314 — Country-neutral contact validation. Edit must
    // enforce the same contract Post Ad enforces: at least one valid
    // contact method (Sudan local OR international with country code).
    const phoneRaw = (phone || '').trim();
    const whatsappRaw = (whatsapp || '').trim();
    const pn = normalizeInternationalPhone(phone);
    const wn = normalizeInternationalPhone(whatsapp);
    if (!phoneRaw && !whatsappRaw) {
      errs.seller_contact = txt(
        'يرجى إدخال رقم هاتف أو واتساب قبل نشر الإعلان.',
        'Please enter a phone or WhatsApp number before publishing the ad.'
      );
    } else if (!pn.valid && !wn.valid) {
      errs.seller_contact = txt(
        'يرجى إدخال رقم هاتف صحيح مع رمز الدولة، مثال: +249963813537.',
        'Please enter a valid phone number with country code, for example +249963813537.'
      );
    } else {
      if (phoneRaw && !pn.valid) {
        errs.seller_phone = txt(
          'رقم الهاتف غير صحيح.',
          'Phone number is invalid.'
        );
      }
      if (whatsappRaw && !wn.valid) {
        errs.seller_whatsapp = txt(
          'رقم الواتساب غير صحيح.',
          'WhatsApp number is invalid.'
        );
      }
    }
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      document.querySelector(`[data-testid="edit-${firstKey}-section"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      toast.error(Object.values(errs)[0]);
      return false;
    }
    return true;
  };

  // Save
  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      // iter314 — Store canonical E.164 (Sudan-local or international)
      // so /api/ads + AdDetails get a predictable shape going forward.
      const phoneNorm = normalizeInternationalPhone(phone);
      const waNorm = normalizeInternationalPhone(whatsapp);
      const payload = {
        title: title.trim(),
        description: description.trim(),
        price: price ? parseFloat(price) : 0,
        currency,
        images,
        location: location.trim(),
        seller_phone: phoneNorm.valid ? phoneNorm.e164 : phone.trim(),
        seller_whatsapp: waNorm.valid ? waNorm.e164 : whatsapp.trim(),
        seller_email: email.trim(),
        category,
        subcategory_id: subcategory,
        subcategory,
      };
      // Discount is ALWAYS sent explicitly — 0 clears any stored discount
      // (previously omitted when 0, so an old discount silently survived).
      payload.discount_percent = discount && parseFloat(discount) > 0 ? parseFloat(discount) : 0;
      if (condition) payload.condition = condition;
      // iter291 — Round-trip ALL preserved vertical fields so service /
      // vehicle / ladies-hub drafts don't lose their type on save.
      // Strip undefined so we don't overwrite stored values with null.
      Object.entries(preservedFields || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null) payload[k] = v;
      });

      const res = await fetch(`${BACKEND_URL}/api/ads/${adId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        // iter291 — FastAPI returns either a string `detail` (our own
        // HTTPException) or a list of dicts with {loc, msg, type}
        // (validation error). Surface the actual failure reason so
        // the user can fix it, instead of the useless generic
        // "Update failed — check your data".
        let msg = '';
        const d = err && err.detail;
        if (typeof d === 'string' && d.trim()) {
          msg = d.trim();
        } else if (Array.isArray(d) && d.length > 0) {
          msg = d
            .map((e) => {
              const field = Array.isArray(e?.loc) ? e.loc[e.loc.length - 1] : '';
              const reason = e?.msg || e?.type || '';
              return field ? `${field}: ${reason}` : reason;
            })
            .filter(Boolean)
            .join(' · ');
        } else if (d && typeof d === 'object' && d.message) {
          msg = d.message;
        }
        if (!msg) msg = txt('فشل التحديث', `Update failed (HTTP ${res.status})`);
        toast.error(msg);
        return;
      }
      toast.success(txt('تم تحديث الإعلان بنجاح', 'Ad updated successfully'));
      if (hasInAppHistory()) navigate(-1);
      else navigate('/my-ads', { replace: true });
    } catch {
      toast.error(txt('فشل الاتصال بالخادم', 'Connection failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryChange = (newCat) => {
    setCategory(newCat);
    setSubcategory('');
    setErrors(prev => ({ ...prev, category: undefined }));
  };

  if (loading) {
    return (
      <div className="edit-ad-page" dir={isArabic ? 'rtl' : 'ltr'}>
        <div className="edit-ad-loading">
          <Loader2 className="edit-ad-spinner" size={28} />
          <span>{txt('جاري التحميل...', 'Loading...')}</span>
        </div>
      </div>
    );
  }

  const isDraft = originalAd?.status === 'draft';

  return (
    <div className="edit-ad-page" dir={isArabic ? 'rtl' : 'ltr'} data-testid="edit-ad-page">
      <AppHeader
        title={isDraft ? txt('تعديل المسودة', 'Edit Draft') : txt('تعديل الإعلان', 'Edit Listing')}
        backFallback="/my-ads"
        testId="edit-ad-header"
      />

      {/* Form Content */}
      <div className="edit-ad-content">

        {/* ── PHOTOS ── */}
        <div className="edit-ad-section" data-testid="edit-photos-section">
          <label className="edit-ad-label">
            {txt('الصور', 'Photos')}
            <span className="edit-ad-label-hint"> ({images.length}/8)</span>
          </label>
          <div className="edit-ad-photos-grid">
            {images.map((url, i) => (
              <div className="edit-ad-photo-item" key={`${url}-${i}`}>
                <img
                  src={url}
                  alt={`Photo ${i + 1}`}
                  className="edit-ad-photo-img"
                  onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50" y="50" text-anchor="middle" dy=".3em" fill="%2394a3b8" font-size="14">?</text></svg>'; }}
                />
                <button className="edit-ad-photo-remove" onClick={() => removeImage(i)} data-testid={`edit-remove-photo-${i}`}>
                  <X size={14} />
                </button>
                {i === 0 && <span className="edit-ad-cover-badge">{txt('الغلاف', 'Cover')}</span>}
              </div>
            ))}
            {images.length < 8 && (
              <label className="edit-ad-photo-add" data-testid="edit-add-photo">
                {uploading
                  ? <Loader2 size={22} className="edit-ad-spinner" />
                  : <Camera size={22} style={{ color: '#16A34A' }} />
                }
                <span style={{ fontSize: 11, fontWeight: 600, color: '#64748B' }}>
                  {uploading ? txt('جاري الرفع...', 'Uploading...') : txt('أضف صورة', 'Add Photo')}
                </span>
                <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={handleImageUpload} disabled={uploading} />
              </label>
            )}
          </div>
        </div>

        {/* ── CATEGORY (shared SelectField) ── */}
        <div className="edit-ad-section" data-testid="edit-category-section">
          <SelectField
            label={`${txt('التصنيف', 'Category')} *`}
            value={category}
            onChange={handleCategoryChange}
            options={categoryOptions}
            placeholder={txt('اختر التصنيف', 'Choose category')}
            title={txt('التصنيف', 'Category')}
            error={errors.category}
            data-testid="edit-category-select"
          />
        </div>

        {/* ── SUBCATEGORY ── */}
        {subcategoryOptions.length > 0 && (
          <div className="edit-ad-section" data-testid="edit-subcategory-section">
            <SelectField
              label={txt('التصنيف الفرعي', 'Subcategory')}
              value={subcategory}
              onChange={v => setSubcategory(v)}
              options={subcategoryOptions}
              placeholder={txt('اختر التصنيف الفرعي', 'Choose subcategory')}
              title={txt('التصنيف الفرعي', 'Subcategory')}
              data-testid="edit-subcategory-select"
            />
          </div>
        )}

        {/* ── TITLE ── */}
        <div className="edit-ad-section" data-testid="edit-title-section">
          <label className="edit-ad-label">{txt('العنوان', 'Title')} *</label>
          <input
            className={`edit-ad-input${errors.title ? ' edit-ad-input-error' : ''}`}
            type="text"
            value={title}
            onChange={e => { setTitle(e.target.value); setErrors(p => ({ ...p, title: undefined })); }}
            placeholder={txt('اسم المنتج أو الخدمة', 'Product or service name')}
            data-testid="edit-title-input"
          />
          {errors.title && <span className="edit-ad-error">{errors.title}</span>}
        </div>

        {/* ── DESCRIPTION ── */}
        <div className="edit-ad-section" data-testid="edit-desc-section">
          <label className="edit-ad-label">{txt('الوصف', 'Description')}</label>
          <textarea
            className="edit-ad-textarea"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder={txt('أضف وصفاً تفصيلياً', 'Add a detailed description')}
            rows={4}
            data-testid="edit-desc-input"
          />
        </div>

        {/* ── PRICING ── */}
        <div className="edit-ad-section" data-testid="edit-price-section">
          <label className="edit-ad-label">{txt('التسعير', 'Pricing')}</label>

          {/* Price (primary, full width) */}
          <div className="ea-price-primary">
            <label className="edit-ad-sublabel">{txt('السعر', 'Price')}</label>
            <input
              className="edit-ad-input"
              type="number"
              inputMode="decimal"
              value={price}
              onChange={e => setPrice(e.target.value)}
              placeholder="0"
              min="0"
              data-testid="edit-price-input"
            />
          </div>

          {/* Currency + Discount side by side */}
          <div className="ea-price-row">
            <div className="ea-price-field ea-price-currency">
              <SelectField
                label={txt('العملة', 'Currency')}
                value={currency}
                onChange={v => setCurrency(v)}
                options={currencyOptions}
                placeholder="SDG"
                title={txt('العملة', 'Currency')}
                data-testid="edit-currency-select"
              />
            </div>
            <div className="ea-price-field ea-price-discount">
              <label className="edit-ad-sublabel">{txt('خصم (اختياري)', 'Discount (opt.)')}</label>
              <input
                className="edit-ad-input"
                type="number"
                inputMode="decimal"
                value={discount}
                onChange={e => setDiscount(e.target.value)}
                placeholder="0"
                min="0"
                data-testid="edit-discount-input"
              />
            </div>
          </div>
        </div>

        {/* ── CONDITION ── */}
        <div className="edit-ad-section" data-testid="edit-condition-section">
          <label className="edit-ad-label">{txt('الحالة', 'Condition')}</label>
          <div className="edit-ad-chips">
            {CONDITIONS.map(c => (
              <button
                key={c.value}
                className={`edit-ad-chip${condition === c.value ? ' edit-ad-chip-active' : ''}`}
                onClick={() => setCondition(condition === c.value ? '' : c.value)}
                data-testid={`edit-condition-${c.value}`}
              >
                {condition === c.value && <Check size={13} style={{ flexShrink: 0 }} />}
                {isArabic ? c.ar : c.en}
              </button>
            ))}
          </div>
        </div>

        {/* ── LOCATION ── */}
        <div className="edit-ad-section" data-testid="edit-location-section">
          <label className="edit-ad-label">{txt('الموقع', 'Location')}</label>
          <input className="edit-ad-input" type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder={txt('المدينة أو المنطقة', 'City or area')} data-testid="edit-location-input" />
        </div>

        {/* ── CONTACT ── */}
        <div className="edit-ad-section" data-testid="edit-contact-section">
          <label className="edit-ad-label">{txt('معلومات التواصل', 'Contact Info')}</label>
          <input className="edit-ad-input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder={txt('رقم الهاتف', 'Phone number')} style={{ marginBottom: 8 }} data-testid="edit-phone-input" />
          <input className="edit-ad-input" type="tel" value={whatsapp} onChange={e => setWhatsapp(e.target.value)} placeholder={txt('واتساب (اختياري)', 'WhatsApp (optional)')} style={{ marginBottom: 8 }} data-testid="edit-whatsapp-input" />
          <input className="edit-ad-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={txt('البريد الإلكتروني (اختياري)', 'Email (optional)')} data-testid="edit-email-input" />
        </div>

        <div style={{ height: 160 }} />
      </div>

      {/* ── STICKY FOOTER ── */}
      <div className="edit-ad-footer" data-testid="edit-ad-footer">
        <button className="edit-ad-save-btn" onClick={handleSave} disabled={saving || !title.trim()} data-testid="edit-save-btn">
          {saving ? <Loader2 size={18} className="edit-ad-spinner" /> : <Save size={18} />}
          <span>{saving ? txt('جاري الحفظ...', 'Saving...') : txt('حفظ التغييرات', 'Save Changes')}</span>
        </button>
      </div>

    </div>
  );
}
